'use client'

import { useState, useEffect } from 'react'
import { Modal, Input, Button, Tag, InputNumber, Switch, Tooltip } from 'antd'
import {
  SearchOutlined,
  InfoCircleOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-height: 80vh;
    overflow-y: auto;
  }
`

const RecipeItem = styled.div`
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #b5d922;
    background-color: #f9fff0;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;
  }
`

const RecipeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const RecipeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const AllergenTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`

const PortionSection = styled.div`
  margin: 16px 0;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 6px;
`

const LockSection = styled.div`
  margin: 16px 0;
  padding: 16px;
  background-color: #f0f8ff;
  border-radius: 6px;
  border: 1px solid #d6f7ff;
`

const WarningBox = styled.div`
  background-color: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  color: #d46b08;
`

// Function to check circular dependencies
const checkCircularDependency = (
  currentRecipeId,
  targetRecipeId,
  allRecipes = [],
) => {
  const visited = new Set()
  const stack = [targetRecipeId]

  while (stack.length > 0) {
    const current = stack.pop()

    if (current === currentRecipeId) {
      return true // Found circular dependency
    }

    if (visited.has(current)) {
      continue
    }

    visited.add(current)

    // Add recipe dependencies to stack (if any)
    const recipe = allRecipes.find(r => r._id === current)
    if (recipe && recipe.recipeIngredients) {
      recipe.recipeIngredients.forEach(ri => stack.push(ri.recipeId))
    }
  }

  return false
}

const AddRecipeIngredientModal = ({
  visible,
  onCancel,
  onAddRecipe,
  availableRecipes = [],
  currentRecipeId,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRecipe, setSelectedRecipe] = useState(null)
  const [portions, setPortions] = useState(1)
  const [isLocked, setIsLocked] = useState(true)
  const [filteredRecipes, setFilteredRecipes] = useState([])

  useEffect(() => {
    if (searchQuery) {
      const filtered = availableRecipes.filter(recipe =>
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      setFilteredRecipes(filtered)
    } else {
      setFilteredRecipes(availableRecipes)
    }
  }, [searchQuery, availableRecipes])

  const handleSelectRecipe = recipe => {
    const hasCircularDep = checkCircularDependency(
      currentRecipeId,
      recipe._id,
      availableRecipes,
    )
    if (hasCircularDep) {
      return // Don't allow selection if circular dependency
    }
    setSelectedRecipe(recipe)
    setPortions(1) // Reset portions
  }

  const handleAddRecipe = () => {
    if (selectedRecipe && portions > 0) {
      const recipeIngredient = {
        id: `recipe-${Date.now()}`,
        recipeId: selectedRecipe._id,
        recipeName: selectedRecipe.title,
        portions: portions,
        isLocked: isLocked,
        costPerPortion: selectedRecipe.costPerPortion || 0,
        totalCost: (selectedRecipe.costPerPortion || 0) * portions,
        allergens: selectedRecipe.allergens || [],
        baseRecipe: selectedRecipe,
      }

      onAddRecipe(recipeIngredient)
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedRecipe(null)
    setPortions(1)
    setIsLocked(true)
    setSearchQuery('')
    onCancel()
  }

  const isCircularDep = recipe => {
    return checkCircularDependency(
      currentRecipeId,
      recipe._id,
      availableRecipes,
    )
  }

  return (
    <StyledModal
      title='Add OMS Recipe'
      visible={visible}
      onCancel={handleClose}
      width={600}
      footer={[
        <Button key='cancel' onClick={handleClose}>
          Cancel
        </Button>,
        selectedRecipe && (
          <Button
            key='add'
            type='primary'
            onClick={handleAddRecipe}
            disabled={!portions || portions <= 0}
          >
            Add Recipe
          </Button>
        ),
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder='Search recipes...'
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          allowClear
        />
      </div>

      {!selectedRecipe ? (
        <div>
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
              marginBottom: 8,
              borderRadius: 4,
            }}
          >
            📖 Saved Recipes
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {filteredRecipes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                No recipes found
              </div>
            ) : (
              filteredRecipes.map(recipe => {
                const hasCircularDep = isCircularDep(recipe)
                return (
                  <Tooltip
                    key={recipe._id}
                    title={
                      hasCircularDep
                        ? 'Cannot add - would create circular reference'
                        : `${recipe.title} - ${
                            recipe.portions || 1
                          } portions | £${(recipe.costPerPortion || 0).toFixed(
                            2,
                          )}/portion`
                    }
                  >
                    <RecipeItem
                      className={hasCircularDep ? 'disabled' : ''}
                      onClick={() =>
                        !hasCircularDep && handleSelectRecipe(recipe)
                      }
                    >
                      <RecipeHeader>
                        <RecipeInfo>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span style={{ fontWeight: 'bold' }}>
                              {recipe.title}
                            </span>
                            <Tag color='blue'>📖 Recipe</Tag>
                            {hasCircularDep && (
                              <Tag color='red'>Circular Ref</Tag>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {recipe.portions || 1} portions | £
                            {(recipe.costPerPortion || 0).toFixed(2)}/portion
                          </div>
                        </RecipeInfo>
                      </RecipeHeader>

                      {recipe.allergens && recipe.allergens.length > 0 && (
                        <AllergenTags>
                          {recipe.allergens.slice(0, 3).map(allergen => (
                            <Tag key={allergen} size='small' color='orange'>
                              {allergen}
                            </Tag>
                          ))}
                          {recipe.allergens.length > 3 && (
                            <Tag size='small'>
                              +{recipe.allergens.length - 3} more
                            </Tag>
                          )}
                        </AllergenTags>
                      )}
                    </RecipeItem>
                  </Tooltip>
                )
              })
            )}
          </div>
        </div>
      ) : (
        <div>
          <RecipeItem
            style={{ backgroundColor: '#f0f8ff', border: '2px solid #b5d922' }}
          >
            <RecipeHeader>
              <RecipeInfo>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {selectedRecipe.title}
                  </span>
                  <Tag color='blue'>📖 Recipe</Tag>
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  Base recipe: {selectedRecipe.portions || 1} portions | £
                  {(selectedRecipe.costPerPortion || 0).toFixed(2)}/portion
                </div>
              </RecipeInfo>
            </RecipeHeader>

            {selectedRecipe.allergens && selectedRecipe.allergens.length > 0 && (
              <AllergenTags>
                {selectedRecipe.allergens.map(allergen => (
                  <Tag key={allergen} size='small' color='orange'>
                    {allergen}
                  </Tag>
                ))}
              </AllergenTags>
            )}
          </RecipeItem>

          <PortionSection>
            <div style={{ marginBottom: 8 }}>
              <strong>Portions to use:</strong>
            </div>
            <InputNumber
              min={0.1}
              step={0.1}
              value={portions}
              onChange={setPortions}
              style={{ width: '100%' }}
              placeholder='Enter number of portions'
            />
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Cost: £
              {((selectedRecipe.costPerPortion || 0) * portions).toFixed(2)}
            </div>
          </PortionSection>

          <LockSection>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  <span style={{ marginRight: 8 }}>
                    {isLocked ? <LockOutlined /> : <UnlockOutlined />}
                  </span>
                  Link to Base Recipe
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  When linked, this recipe will always reflect the latest
                  ingredients, prices, and allergens from the base recipe.
                </div>
              </div>
              <Switch checked={isLocked} onChange={setIsLocked} />
            </div>

            {isLocked && (
              <WarningBox>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                Warning: Changes to the base recipe will automatically update
                this recipe and may affect cost or allergen values.
              </WarningBox>
            )}
          </LockSection>
        </div>
      )}
    </StyledModal>
  )
}

export default AddRecipeIngredientModal
